import { useState } from 'react';
import { Button, Field, Input, Spinner, makeStyles } from '@fluentui/react-components';
import { exitPublicDemoAndReload } from '../lib/publicDemo.js';

const useStyles = makeStyles({
  page: {
    minHeight: '100vh',
    display: 'grid',
    placeItems: 'center',
    padding: '24px',
    background:
      'radial-gradient(120% 80% at 50% -10%, rgba(12,111,214,0.18) 0%, transparent 55%), var(--bg-page, #0f1419)',
  },
  card: {
    width: 'min(440px, 100%)',
    padding: '28px 28px 24px',
    borderRadius: '16px',
    background: 'var(--bg-surface, #1a2332)',
    border: '1px solid var(--border-default, rgba(255,255,255,0.12))',
    boxShadow: '0 24px 64px rgba(0,0,0,0.35)',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },
  brand: {
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'var(--opus-brand-60, #0c6fd6)',
  },
  title: {
    margin: 0,
    fontFamily: 'var(--font-display, inherit)',
    fontSize: '26px',
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: '-0.02em',
    color: 'var(--text-primary, #e8eef7)',
  },
  sub: {
    margin: 0,
    fontSize: '14px',
    lineHeight: 1.5,
    color: 'var(--text-secondary, #9aa8bc)',
  },
  err: { margin: 0, color: 'var(--bad, #ef4444)', fontSize: '13px' },
  note: {
    margin: 0,
    fontSize: '12px',
    color: 'var(--text-tertiary, #7a8799)',
    lineHeight: 1.4,
  },
});

function isWorkEmail(email: string): boolean {
  const e = email.trim().toLowerCase();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) return false;
  return true;
}

export function DemoLeadGate({
  onSubmit,
  canExit = false,
}: {
  onSubmit: (data: { name: string; email: string }) => Promise<void>;
  /**
   * Solo cuando la demostración se prendió a mano (`?demo=1`, típicamente en la
   * máquina de quien desarrolla): ofrece la puerta de vuelta a la app normal.
   * En el sitio público esta salida no aparece — ahí no hay a dónde volver.
   */
  canExit?: boolean;
}) {
  const styles = useStyles();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    const n = name.trim();
    const em = email.trim().toLowerCase();
    if (n.length < 2) {
      setErr('Escribe tu nombre.');
      return;
    }
    if (!isWorkEmail(em)) {
      setErr('Escribe un correo de trabajo válido.');
      return;
    }
    setBusy(true);
    try {
      await onSubmit({ name: n, email: em });
    } catch (ex) {
      setErr((ex as Error).message || 'No se pudo entrar al demo.');
      setBusy(false);
    }
  }

  return (
    <div className={styles.page}>
      <form className={styles.card} onSubmit={(e) => void handleSubmit(e)}>
        <div className={styles.brand}>OPUS ai · Demostración</div>
        <h1 className={styles.title}>En 5 minutos sabrás que SÍ es para ti.</h1>
        <p className={styles.sub}>
          Déjanos tu nombre y correo de trabajo para recorrer un concurso de muestra ya procesado
          (datos de muestra de Ecosoft).
        </p>
        <Field label="Nombre">
          <Input
            value={name}
            onChange={(_, d) => setName(d.value)}
            placeholder="Tu nombre"
            autoComplete="name"
            disabled={busy}
          />
        </Field>
        <Field label="Correo de trabajo">
          <Input
            type="email"
            value={email}
            onChange={(_, d) => setEmail(d.value)}
            placeholder="nombre@empresa.com"
            autoComplete="email"
            disabled={busy}
          />
        </Field>
        {err && <p className={styles.err}>{err}</p>}
        <Button appearance="primary" type="submit" disabled={busy} icon={busy ? <Spinner size="tiny" /> : undefined}>
          {busy ? 'Entrando…' : 'Entrar al demo'}
        </Button>
        <p className={styles.note}>Solo lectura · sin consumo de créditos · datos de muestra de Ecosoft</p>
        {canExit && (
          <Button
            type="button"
            appearance="transparent"
            size="small"
            onClick={() => exitPublicDemoAndReload()}
          >
            Salir de la demostración e iniciar sesión
          </Button>
        )}
      </form>
    </div>
  );
}
